import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Skeleton } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso'
import styled from 'styled-components'
import { Button as SutraButton } from 'sutra/components/Button'
import { Input as SearchInput } from 'sutra/components/Input/Input'
import { Popconfirm } from 'sutra/components/Popconfirm/Popconfirm'
import { Select as SutraSelect } from 'sutra/components/Select/Select'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'
import { accents, gray } from 'sutra/tokens/colors'

import {
  ALIAS_FOR_NULL_VALUES,
  isNullValueAlias,
} from 'src/constants/filter-values'

const NULL_VALUE = 'Null'

// Shared by every count caption in this popover ("All Properties (N)",
// "Select all (N)", the field-detail heading, brand/theme group counts) —
// mirrors the v1 filter picker's identical `SelectItem.tsx` pattern.
const numberFormatter = new Intl.NumberFormat()

// Cancel + Apply row for Apply mode (see `onApplySelections`), pinned under
// the sidebar/value columns.
const ApplyDraftFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid ${gray[200]};
`

const FilterOptionsSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px;
`

const FilterOptionSkeletonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  height: 20px;

  .ant-skeleton {
    flex: 1;
  }
`

// Left sidebar row. Hover/selected paint via CSS instead of a React
// `hoveredItem` useState + `e.currentTarget.style.*` writes — mirrors
// `HoverableValueRow`'s doc comment: moving the mouse between rows shouldn't
// re-render the popover tree.
const SidebarItem = styled.div<{
  $selected: boolean
  $disabled: boolean
  $bold?: boolean
}>`
  padding: 8px 12px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
  transition: background-color 0.2s ease;
  color: ${({ $disabled }) => ($disabled ? gray[400] : 'inherit')};
  background-color: ${({ $selected }) =>
    $selected ? accents.reflectionBlue[100] : 'transparent'};
  border-left: 2px solid
    ${({ $selected }) =>
      $selected ? accents.reflectionBlue[500] : 'transparent'};
  ${({ $selected, $disabled }) =>
    !$selected &&
    !$disabled &&
    `&:hover {
      background-color: ${accents.reflectionBlue[100]};
    }`}
`

// Leading type mark on a sidebar row (`#` numeric, `Aa` text).
const SidebarItemGlyph = styled.span`
  flex-shrink: 0;
  width: 14px;
  margin-right: 8px;
  font-family: 'SF Mono', Menlo, monospace;
  font-size: 10px;
  color: ${gray[400]};
`

// Non-clickable subject header ("Hub", "Language", …) in the left sidebar.
const SidebarGroupHeader = styled.div`
  padding: 8px 14px 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${gray[500]};
  cursor: default;
`

// Shared chrome for "Select all" and the brand/theme group-collapse headers —
// four call sites previously repeated the same backgroundColor/border inline.
const GroupHeaderRow = styled.div<{ $padding: string; $marginBottom: string }>`
  display: flex;
  align-items: center;
  padding: ${({ $padding }) => $padding};
  cursor: pointer;
  border-radius: 6px;
  margin-bottom: ${({ $marginBottom }) => $marginBottom};
  background-color: ${gray[50]};
  border: 1px solid ${gray[200]};
`

import { FilterOptionPostCountCell } from 'features/charts-v2/components/filter-option-post-count-cell'
import {
  HoverableValueRow,
  PaneRowDeleteButton,
  PaneRowOnlyButton,
  SmallCheckbox,
  SortSelectWrap,
} from 'features/charts-v2/components/filter-popover-pane.styles'
import {
  getPopoverLayout,
  POPOVER_MAX_HEIGHT,
  POPOVER_MAX_WIDTH,
  type PopoverLayout,
} from 'features/charts-v2/components/filter-selection-popover-layout'
import { FilterPopoverFreeInputPane } from 'features/charts-v2/components/FilterPopoverFreeInputPane/FilterPopoverFreeInputPane'
import { FilterPopoverKeywordsPane } from 'features/charts-v2/components/FilterPopoverKeywordsPane/FilterPopoverKeywordsPane'
import { FilterPopoverNumericPane } from 'features/charts-v2/components/FilterPopoverNumericPane/FilterPopoverNumericPane'
import { FilterPopoverRangePane } from 'features/charts-v2/components/FilterPopoverRangePane/FilterPopoverRangePane'
import { FilterPopoverRatingPane } from 'features/charts-v2/components/FilterPopoverRatingPane/FilterPopoverRatingPane'
import {
  FilterPopoverSentimentPane,
  type SentimentCommitted,
  type SentimentPaneRestriction,
} from 'features/charts-v2/components/FilterPopoverSentimentPane/FilterPopoverSentimentPane'
import { FilterPopoverSmartSearchPane } from 'features/charts-v2/components/FilterPopoverSmartSearchPane/FilterPopoverSmartSearchPane'
import { type OperatorType as KeywordOperatorType } from 'features/charts-v2/components/KeywordOperatorPopover'
import { type SentimentMode } from 'features/charts-v2/components/SentimentFilterCard'
import {
  type FilterSelectionDraft,
  NUMERIC_FILTER_LABELS,
  type NumericFilterFieldType,
  type NumericOperatorType,
} from 'features/charts-v2/types'
import type { FilterRestriction } from 'features/charts-v2/types/filter-restriction'
import { deriveSentimentMode } from 'features/charts-v2/utils/derive-sentiment-mode'
import { deduplicateFilterValues } from 'features/charts-v2/utils/filter-dedup'
import { buildGroupedSidebarItems } from 'features/charts-v2/utils/filter-sidebar-groups'
import {
  isSidebarItemSelected,
  type LeftSidebarItem,
  type PopoverPane,
} from 'features/charts-v2/utils/is-sidebar-item-selected'
import { matchesSearchTerms } from 'features/charts-v2/utils/match-search-terms'
import {
  type FilterValueSortBy,
  type FilterValueSortDirection,
  sortFilterOptionValues,
} from 'features/charts-v2/utils/sort-filter-option-values'
import { groupThemeValuesByParent } from 'features/charts-v2/utils/theme-hierarchy'
// bully ignore[no-cross-feature-private-imports: sibling cross-feature hook imports across the codebase already violate this rule — the team's de facto practice treats query / orchestrator hooks as a cross-feature public seam (see features/dashboards-v2/hooks/use-dashboard-effective-filter-list.ts's identical import of use-effective-filter-list). follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { useSavedFilterSelectorState } from 'features/filters/hooks/use-saved-filter-selector-state'
import {
  FilterOption,
  FilterOptionRange,
  FilterOptionSelect,
  FilterOptionValue,
  FilterTypes,
  type SavedFilterPreset,
  type YogiQLFilterPreset,
} from 'features/filters/types'
import { useEscapeDismiss } from 'hooks/use-escape-dismiss'
import { FilterPreset } from 'types/project'

// The `verified` field never returns a null bucket from the backend (absent
// values coalesce into "Not Verified"), so its "+ Filter" list lacks the
// "No Value" option that other select fields get from their data. Inject one.
// n_posts: 0 (NOT undefined) so the count cell shows "0 posts" instead of a
// perpetual loading skeleton, and the count-desc sort stays stable (sorts last).
// The value ALIAS_FOR_NULL_VALUES is recognized by isNullValueAlias, so it
// renders as "No Value" and round-trips as IS_NULL via summary-utils.
// Idempotent: if the backend ever starts returning a null bucket, the guard
// prevents double-injection.
const withSyntheticNullOption = (
  filter: FilterOptionSelect,
): FilterOptionSelect => {
  const values = filter.values ?? []
  if (values.some((v) => isNullValueAlias(v.value))) return filter
  return {
    ...filter,
    values: [
      ...values,
      { value: ALIAS_FOR_NULL_VALUES, identifiers: [], n_posts: 0 },
    ],
  }
}

/**
 * Stable identity for one keyboard-navigable row, across the four shapes
 * `getNavigableItems` can return (a field's values, the field list itself, the
 * flattened "All Properties" rows, saved presets).
 *
 * Used to move the highlight WITH its item when the list re-sorts under it —
 * which happens whenever per-field counts land or drop out. Shape-tagged so a
 * value named the same as a field can never collide, and prefixed per shape so
 * the fallback (`''`) can only match another absent item.
 */
const navigableItemKey = (item: unknown): string => {
  if (item === null || typeof item !== 'object') return ''
  if ('displayText' in item && 'parentField' in item) {
    return `flat:${String(item.parentField)}:${String(item.displayText)}`
  }
  // A `FilterOptionValue`'s `value` is unique within one field's list; the
  // theme/dataset lists carry `cluster_uuid` to separate same-named rows.
  if ('value' in item) {
    const clusterUuid = 'cluster_uuid' in item ? String(item.cluster_uuid) : ''
    return `value:${String(item.value)}:${clusterUuid}`
  }
  if ('field' in item) return `field:${String(item.field)}`
  if ('name' in item) return `preset:${String(item.name)}`
  return ''
}

interface FilterSelectionPopoverProps {
  filterOptions: FilterOption[]
  isLoading?: boolean
  precomputedFlattenedValues: Array<{
    parentField: string
    parentAlias: string
    value: FilterOptionValue
    displayText: string
    // A theme parent's row: selects every child cluster uuid at once.
    kind?: 'parent'
  }>
  onFilterSelect: (
    field: string,
    values?: string[],
    rangeData?: { min?: number; max?: number; valueType?: number },
  ) => void
  onClose: () => void
  getFilterSelection: (field: string) => {
    operator: string
    values: string[]
    min?: number
    max?: number
    valueType?: number
    numericValue?: number
  } | null
  triggerElement?: HTMLElement | null
  // 'right' (default) anchors off the trigger's right edge — correct for a
  // sidebar with open room to its right, and pulled back so it never runs
  // past the viewport when there isn't (see `getPopoverLayout`). 'center' centers the
  // popover horizontally in the viewport (not on the trigger — a caller's
  // triggers can be different shapes/positions, e.g. a right-aligned [+]
  // button vs. a full-width "+ Add filters" box, and centering on either
  // one individually would put the popover in a different place depending
  // on which was clicked) — for a caller with no open room to the trigger's
  // right (e.g. a centered inline form, or a right-hand drawer), where
  // 'right' would have to be pulled back over its own trigger.
  anchorMode?: 'right' | 'center'
  // Explicit portal target, for a caller that owns its own host element
  // rather than the default `document.body` — see the fallback's doc
  // comment below. Kept for AgentFilterPanel / UniverseFilterChiclets,
  // which need a stable node across an agent-type swap (an ambient id
  // lookup would race the outgoing panel's unmount there).
  popoverPortalTarget?: HTMLElement | null
  // Currently-committed state for the four special panes, so each pane can
  // pre-fill from the panel and Apply can edit in place rather than replace.
  keywordFilters?: { [key: string]: string[] | null }
  smartSearchValue?: string | null
  onKeywordFiltersApply?: (filters: {
    [key in KeywordOperatorType]?: string[] | null
  }) => void
  onKeywordFiltersClear?: () => void
  onSmartSearchApply?: (value: string) => void
  onSmartSearchClear?: () => void
  onNumericFilterApply?: (
    field: NumericFilterFieldType,
    operator: NumericOperatorType,
    value: number,
  ) => void
  onNumericFilterClear?: (field: NumericFilterFieldType) => void
  onSentimentApply?: (draft: {
    mode: SentimentMode
    values: string[]
    numericOperator: NumericOperatorType
    numericValue: number | undefined
  }) => void
  onSentimentClear?: () => void
  /**
   * Reports the field whose per-value counts this popover is showing, so the
   * parent can fetch counts for just that field. `null` on the "All
   * Properties" view (which shows no counts) and on unmount. PROD-3136.
   */
  onSelectedFieldChange?: (field: string | null) => void
  onSavedFilterApply?: (preset: FilterPreset) => void
  // When set, the saved filters are the YogiQL list and a click applies here.
  onSavedQueryApply?: (preset: YogiQLFilterPreset) => void
  // Rating/Sentiment pane restrictions that can't be expressed by narrowing
  // `filterOptions` alone (valueType, include-null, sentiment mode) — see
  // `deriveFilterRestriction`'s doc comment. `filterOptions` itself is
  // already narrowed by the caller (FilterControl via `restrictFilterOptions`).
  restriction?: FilterRestriction
  // For a picker scoped to a single dimension (e.g. the benchmarking agent's
  // Focus card, narrowed to just `brand`/`product_hierarchy`) — suppresses
  // the hardcoded utility rows (Saved filters, Keywords, Smart search,
  // Records, Sentences, Sentiment, All) that `filterOptions` narrowing alone
  // can't remove, since they're unconditional literals in `leftSidebarItems`,
  // not derived from `filterOptions`. Those panes are inert for such callers
  // anyway (no smart-search/keyword handlers wired). Default false — every
  // existing caller keeps the full sidebar.
  fieldsOnlySidebar?: boolean
  // Fields this picker greys out and refuses to open: Records / Sentences
  // (`record_count` / `sentence_count`), Rating (its own field id), and the
  // Sentiment row (when `sentiment` is listed). `note` is the tooltip that
  // explains why. Unlike `fieldsOnlySidebar` the rows stay visible, so the
  // user sees what exists but can't be used here (a dashboard widget tile).
  // Default undefined: every existing caller keeps every row enabled.
  disabledFields?: { fields: ReadonlySet<string>; note: string }
  // Opens on a given field's values, or on one of the special panes, instead
  // of the "All Properties" view — for a trigger that already knows what the
  // user wants (a filter chip's field, the search bar's Keywords mode). Read
  // once on mount; the popover is unmounted on close, so each open starts
  // fresh. Default undefined: every existing caller opens as before.
  initialSelection?: PopoverInitialSelection
  // When set, the checkbox value lists (a field's values, Select all, the
  // brand groups, the "All Properties" list) only DRAFT: nothing reaches
  // `onFilterSelect` until the footer's Apply, which hands every drafted
  // field over in this ONE call and closes the popover; closing any other
  // way discards the draft. For a caller where each commit is expensive —
  // the dashboard's global filter, where one write refetches every tile.
  // Panes with their own Apply (Rating, free input, Keywords, Smart search,
  // Records/Sentences, Sentiment) still commit on their own Apply. Default
  // undefined: every existing caller commits per click, as before.
  onApplySelections?: (selections: FilterSelectionDraft[]) => void
}

export type PopoverInitialSelection = {
  field?: string
  pane?: PopoverPane
}

export const FilterSelectionPopover: React.FC<FilterSelectionPopoverProps> = ({
  filterOptions,
  isLoading = false,
  precomputedFlattenedValues,
  onFilterSelect: commitFilterSelect,
  onClose,
  getFilterSelection: getCommittedSelection,
  triggerElement,
  anchorMode = 'right',
  popoverPortalTarget,
  keywordFilters = {},
  smartSearchValue = null,
  onKeywordFiltersApply,
  onKeywordFiltersClear,
  onSmartSearchApply,
  onSmartSearchClear,
  onNumericFilterApply,
  onNumericFilterClear,
  onSentimentApply,
  onSentimentClear,
  onSelectedFieldChange,
  onSavedFilterApply,
  onSavedQueryApply,
  restriction,
  fieldsOnlySidebar = false,
  disabledFields,
  initialSelection,
  onApplySelections,
}) => {
  // Apply mode (see `onApplySelections`): checkbox selections land in this
  // per-field draft instead of committing. `getFilterSelection` /
  // `onFilterSelect` below are the draft-aware versions every checkbox list
  // reads and writes, so none of them needs to know which mode it's in.
  const requiresApply = !!onApplySelections
  const [draftValuesByField, setDraftValuesByField] = useState<
    Record<string, string[]>
  >({})
  const getFilterSelection = useCallback(
    (field: string) => {
      const committed = getCommittedSelection(field)
      const drafted = draftValuesByField[field]
      if (!requiresApply || drafted === undefined) return committed
      return {
        ...committed,
        operator: committed?.operator ?? 'is',
        values: drafted,
      }
    },
    [getCommittedSelection, requiresApply, draftValuesByField],
  )
  const onFilterSelect = useCallback(
    (...args: Parameters<FilterSelectionPopoverProps['onFilterSelect']>) => {
      const [field, values, rangeData] = args
      if (requiresApply && !rangeData) {
        setDraftValuesByField((prev) => ({ ...prev, [field]: values ?? [] }))
        return
      }
      // Forward the caller's exact arguments (no padded `undefined`s).
      commitFilterSelect(...args)
    },
    [requiresApply, commitFilterSelect],
  )
  // Only fields whose draft differs from what's committed — toggling a value
  // on and back off leaves nothing to apply.
  const changedDrafts: FilterSelectionDraft[] = Object.entries(
    draftValuesByField,
  )
    .filter(([field, values]) => {
      const committed = getCommittedSelection(field)?.values ?? []
      return (
        values.length !== committed.length ||
        values.some((v) => !committed.includes(v))
      )
    })
    .map(([field, values]) => ({ field, values }))
  const handleApplyDraft = (): void => {
    if (changedDrafts.length > 0) onApplySelections?.(changedDrafts)
    onClose()
  }

  const [searchTerm, setSearchTerm] = useState('')
  const [view, setView] = useState<'fields' | 'saved'>('fields')
  // An `initialSelection` naming a disabled field/pane opens the unselected
  // view instead — the sidebar row is greyed out, so its pane must not open
  // through the back door either.
  const [selectedField, setSelectedField] = useState<string | null>(
    initialSelection?.field &&
      !disabledFields?.fields.has(initialSelection.field)
      ? initialSelection.field
      : null,
  )
  // Declared up here rather than with the other pane state below because the
  // sort menu and the counts report both read `isSentimentPane`.
  const [pane, setPane] = useState<PopoverPane | null>(
    initialSelection?.pane && !disabledFields?.fields.has(initialSelection.pane)
      ? initialSelection.pane
      : null,
  )
  const isSentimentPane = pane === 'sentiment'
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0)
  const [selectMyBrands, setSelectMyBrands] = useState(false)
  const [selectCompetitorBrands, setSelectCompetitorBrands] = useState(false)
  const [myBrandsExpanded, setMyBrandsExpanded] = useState(true)
  const [competitorBrandsExpanded, setCompetitorBrandsExpanded] = useState(true)
  // Hover and focus state previously lived here. Co-located now: each
  // row's `:hover` and the search input's `:focus` drive their own
  // visuals via CSS. Moving the mouse between rows or focusing the
  // search no longer re-renders the popover tree.

  // Saved filter presets — fetched from the shared react-query cache used by v1
  const {
    data: savedPresets,
    isLoading: savedPresetsLoading,
    refetch: refetchSavedPresets,
    filterService,
    projectId,
  } = useSavedFilterSelectorState(!!onSavedQueryApply)
  const applySavedPreset = (preset: SavedFilterPreset): void => {
    if ('query' in preset) onSavedQueryApply?.(preset)
    else onSavedFilterApply?.(preset)
  }

  // Single sort dropdown — encodes (sortBy, sortDirection) into one key
  // so the four user-facing options live in one Select.
  type SortKey = 'count-desc' | 'count-asc' | 'title-asc' | 'title-desc'
  const [sortKey, setSortKey] = useState<SortKey>('count-desc')

  const SORT_OPTIONS: Array<{ value: SortKey; label: string }> = [
    { value: 'count-desc', label: 'Record count: high to low' },
    { value: 'count-asc', label: 'Record count: low to high' },
    { value: 'title-asc', label: 'Alphabetical, A-Z' },
    { value: 'title-desc', label: 'Alphabetical, Z-A' },
  ]

  // Counts are fetched only for the field the user has opened, so the "All
  // Properties" view has no counts to sort by. The record-count options stay
  // visible there but are disabled (see `sortMenuItems`), and the effective
  // sort falls back to A-Z when the sticky `sortKey` is a count sort —
  // otherwise the menu would advertise a record-count sort while
  // `flattenedChildValues` silently degraded it to alphabetical.
  const countSortAvailable = selectedField !== null || isSentimentPane
  const effectiveSortKey: SortKey =
    countSortAvailable || sortKey.startsWith('title') ? sortKey : 'title-asc'

  const { sortBy, sortDirection } = useMemo<{
    sortBy: FilterValueSortBy
    sortDirection: FilterValueSortDirection
  }>(() => {
    switch (effectiveSortKey) {
      case 'count-desc':
        return { sortBy: 'count', sortDirection: 'DESC' }
      case 'count-asc':
        return { sortBy: 'count', sortDirection: 'ASC' }
      case 'title-asc':
        return { sortBy: 'title', sortDirection: 'ASC' }
      case 'title-desc':
        return { sortBy: 'title', sortDirection: 'DESC' }
    }
  }, [effectiveSortKey])

  const sortOptions = useMemo(
    () => ({ sortBy, sortDirection }),
    [sortBy, sortDirection],
  )

  // Greyed out rather than hidden on "All Properties": the option still tells
  // the reader that a record-count sort exists. `SutraSelect` renders a
  // disabled option in gray[400] with `cursor: not-allowed` and ignores
  // clicks on it, so the sticky `sortKey` cannot be moved onto a count sort
  // from a view that has no counts.
  const sortSelectOptions = SORT_OPTIONS.map((opt) => ({
    ...opt,
    disabled: !countSortAvailable && opt.value.startsWith('count'),
  }))

  const sortDropdown = (
    <SortSelectWrap onClick={(e) => e.stopPropagation()}>
      <SutraSelect
        value={effectiveSortKey}
        onChange={(picked) => setSortKey(picked)}
        options={sortSelectOptions}
        triggerLabel="Sort"
        panelAlign="end"
        zIndex={2001}
      />
    </SortSelectWrap>
  )

  const searchInputRef = useRef<HTMLInputElement>(null)
  const highlightedItemRef = useRef<HTMLDivElement>(null)
  const virtuosoRef = useRef<VirtuosoHandle>(null)

  // Auto-focus the search input when the popover opens
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [])

  useEscapeDismiss({ onClose })

  // Report the field whose counts this pane is displaying so the parent
  // fetches counts for that field only. The sentiment pane shows the
  // `sentiment` option's values without selecting the field, so it names
  // `sentiment` explicitly. The cleanup covers every close path (both the
  // trigger button and the outside-click overlay unmount the popover), so
  // the fetch gates off again when the popover goes away. PROD-3136.
  const fieldForCounts = isSentimentPane ? 'sentiment' : selectedField
  useEffect(() => {
    onSelectedFieldChange?.(fieldForCounts)
    return () => onSelectedFieldChange?.(null)
  }, [fieldForCounts, onSelectedFieldChange])

  // Helper to get the identifier for special filters (theme, dataset) that use cluster_uuid
  const getSpecialFilterIdentifier = (
    value: FilterOptionValue,
    field: string,
  ): string => {
    // For theme and proj_uuid filters, use cluster_uuid if available
    if ((field === 'theme' || field === 'proj_uuid') && value.cluster_uuid) {
      return value.cluster_uuid
    }
    return value.value
  }

  // Handle checkbox selection
  // For theme and dataset filters, pass the FilterOptionValue to use cluster_uuid
  const handleValueToggle = (
    value: string | FilterOptionValue,
    useClusterUuid: boolean = false,
  ): void => {
    if (!selectedField) return

    const existingFilter = getFilterSelection(selectedField)
    const currentValues = existingFilter?.values || []

    // For de-duped theme rows with multiple identifiers, toggle all of them
    if (
      useClusterUuid &&
      typeof value !== 'string' &&
      value.identifiers &&
      value.identifiers.length > 0
    ) {
      const allIds = value.identifiers
      const anySelected = allIds.some((id) => currentValues.includes(id))

      const newSelectedValues = anySelected
        ? currentValues.filter((v) => !allIds.includes(v))
        : [...currentValues, ...allIds]

      onFilterSelect(selectedField, newSelectedValues)
    } else {
      // Single identifier toggle
      const identifier =
        useClusterUuid && typeof value !== 'string'
          ? getSpecialFilterIdentifier(value, selectedField)
          : typeof value === 'string'
            ? value
            : value.value

      const newSelectedValues = currentValues.includes(identifier)
        ? currentValues.filter((v) => v !== identifier)
        : [...currentValues, identifier]

      onFilterSelect(selectedField, newSelectedValues)
    }
  }

  // Handle select all toggle — merges/removes the currently-visible (search-
  // filtered) values rather than replacing the whole selection. This means
  // selecting-all under one search term then selecting-all under another adds
  // to the selection instead of clobbering the first batch.
  const handleSelectAll = (): void => {
    if (!selectedField || !rightSideContent[0]) return

    const selectFilter = rightSideContent[0] as FilterOptionSelect
    // Use cluster_uuid for theme/dataset filters, value for others
    const visibleValues =
      selectFilter.values?.map((v) =>
        useClusterUuid ? getSpecialFilterIdentifier(v, selectedField) : v.value,
      ) ?? []

    const currentValues = getFilterSelection(selectedField)?.values ?? []
    const allVisibleAlreadySelected =
      visibleValues.length > 0 &&
      visibleValues.every((v) => currentValues.includes(v))

    const newSelectedValues = allVisibleAlreadySelected
      ? currentValues.filter((v) => !visibleValues.includes(v))
      : [
          ...currentValues.filter((v) => !visibleValues.includes(v)),
          ...visibleValues,
        ]

    onFilterSelect(selectedField, newSelectedValues)
  }

  // Filter out fields that are handled separately (date) or shouldn't be shown (star/favorited)
  // This matches the behavior of Charts v1
  // Memoized so the `rightSideContent` -> `sortedValuesForSelectedField` chain
  // below can actually hold: those memos take this as a dependency, and a fresh
  // array every render made both of them recompute on every keystroke.
  const filteredFilterOptions = useMemo(
    () =>
      filterOptions.filter(
        (filter) => filter.field !== 'star' && filter.field !== 'create_time',
      ),
    [filterOptions],
  )

  // Left sidebar rows. The full sidebar is `buildGroupedSidebarItems` (subject
  // groups); the three option lists below feed only the fields-only sidebar.
  // Exclude sentiment/post_sentiment (covered by the Sentiment pane) and
  // score/post_score / proj_uuid, which that sidebar lists first.
  const regularFilterOptions = filteredFilterOptions.filter(
    (filter) =>
      filter.field !== 'sentiment' &&
      filter.field !== 'post_sentiment' &&
      filter.field !== 'score' &&
      filter.field !== 'post_score' &&
      filter.field !== 'proj_uuid',
  )

  // Rating is score or post_score, whichever field is present in this project.
  const ratingFilterOption = filteredFilterOptions.find(
    (f) => f.field === 'score' || f.field === 'post_score',
  )

  // Dataset (proj_uuid) is listed ahead of the other fields.
  const datasetFilterOption = filteredFilterOptions.find(
    (f) => f.field === 'proj_uuid',
  )

  // A picker scoped to a single dimension skips every hardcoded utility row —
  // only the data-driven field entries remain. See `fieldsOnlySidebar`'s doc
  // comment on the props interface.
  const leftSidebarItems: LeftSidebarItem[] = fieldsOnlySidebar
    ? [
        ...(datasetFilterOption
          ? [
              {
                name: datasetFilterOption.alias || 'Dataset',
                field: datasetFilterOption.field,
                type: 'field' as const,
              },
            ]
          : []),
        ...(ratingFilterOption
          ? [
              {
                name: ratingFilterOption.alias || 'Rating',
                field: ratingFilterOption.field,
                type: 'field' as const,
              },
            ]
          : []),
        ...regularFilterOptions.map((filter) => ({
          name: filter.alias || filter.field,
          field: filter.field,
          type: 'field' as const,
        })),
      ]
    : buildGroupedSidebarItems(filteredFilterOptions)

  // Fields-only pickers show exactly one field row (the caller has already
  // narrowed `filterOptions` to a single dimension) — open directly on its
  // value list instead of the empty "select a field" state. Effect (not a
  // lazy initializer) because `filterOptions` can still be loading when the
  // popover first mounts.
  const onlyFieldItem =
    fieldsOnlySidebar && leftSidebarItems.length === 1
      ? leftSidebarItems[0]
      : null
  const onlyField =
    onlyFieldItem && 'field' in onlyFieldItem ? onlyFieldItem.field : null
  useEffect(() => {
    if (onlyField && selectedField !== onlyField) {
      setSelectedField(onlyField)
    }
  }, [onlyField, selectedField])

  // Smart search and keyword search are mutually exclusive — only one text
  // search function can be applied at once. When one is active, the other is
  // disabled (greyed out) in the panel, but both items always remain visible
  // so the panel stays consistent with every other filter category. Derived
  // directly from the committed panel state rather than mirrored booleans.
  const isSmartSearchActive = smartSearchValue !== null
  const isKeywordsActive = Object.values(keywordFilters).some(
    (values) => values !== null,
  )
  const isSearchItemDisabled = (
    type: (typeof leftSidebarItems)[number]['type'],
  ): boolean =>
    (type === 'keywords' && isSmartSearchActive) ||
    (type === 'smart_search' && isKeywordsActive)

  // Why a sidebar row can't be clicked, or null when it can. The `disabledFields`
  // reasons (caller says "not usable here") and the search exclusivity share
  // one answer, so the greyed-out style, the click guard and the tooltip can't
  // disagree. Rating is a plain field row, Records / Sentences are numeric rows
  // keyed by `numericField`, and the Sentiment row is keyed by `sentiment`.
  const getSidebarItemDisabledReason = (
    item: (typeof leftSidebarItems)[number],
  ): string | null => {
    if (isSearchItemDisabled(item.type)) {
      return 'Only one search type can be active at a time.'
    }
    if (!disabledFields) return null
    const itemField =
      item.type === 'field'
        ? item.field
        : item.type === 'numeric'
          ? item.numericField
          : item.type === 'sentiment'
            ? 'sentiment'
            : null
    return itemField !== null && disabledFields.fields.has(itemField)
      ? disabledFields.note
      : null
  }

  // Get the content for the right side based on selection. Memoized because
  // `sortedValuesForSelectedField` and `navCountsKnown` take it as a dependency
  // and would otherwise re-sort the whole value list on every render.
  const rightSideContent = useMemo<FilterOption[]>(() => {
    if (selectedField) {
      // Show values for the selected field, filtered by search term
      const selectedFilter = filteredFilterOptions.find(
        (f) => f.field === selectedField,
      )
      if (!selectedFilter) return []

      // Inject a synthetic "No Value" option for `verified` — the backend
      // coalesces absent values to "Not Verified", so the null bucket never
      // arrives from the API and must be synthesised client-side.
      const effectiveFilter =
        selectedFilter.field.toLowerCase() === 'verified' &&
        selectedFilter.filter_type === 'select'
          ? withSyntheticNullOption(selectedFilter as FilterOptionSelect)
          : selectedFilter

      // If there's a search term, filter the values within this field
      if (searchTerm && effectiveFilter.filter_type === 'select') {
        const selectFilter = effectiveFilter as FilterOptionSelect
        const filteredValues =
          selectFilter.values?.filter((value) =>
            matchesSearchTerms(value.value, searchTerm),
          ) || []

        return [
          {
            ...effectiveFilter,
            values: filteredValues,
          },
        ]
      }

      return [effectiveFilter]
    } else {
      // Show all filter options (when "All" is selected)
      return filteredFilterOptions.filter(
        (option) =>
          searchTerm === '' ||
          matchesSearchTerms(option.field, searchTerm) ||
          (option.alias && matchesSearchTerms(option.alias, searchTerm)),
      )
    }
  }, [selectedField, filteredFilterOptions, searchTerm])

  // Filter pre-computed values by search term (lightweight operation),
  // then apply the active sort.
  const flattenedChildValues = useMemo(() => {
    if (selectedField) return []
    const filtered = searchTerm
      ? precomputedFlattenedValues.filter((item) =>
          matchesSearchTerms(item.displayText, searchTerm),
        )
      : precomputedFlattenedValues
    const copy = [...filtered]
    if (sortBy === 'count') {
      // Mixed-known counts fall back to alphabetical, matching
      // sortFilterOptionValues' contract.
      const allCountsKnown = copy.every(
        (item) => item.value.n_posts !== undefined,
      )
      if (allCountsKnown) {
        copy.sort((a, b) => {
          const aCount = a.value.n_posts ?? 0
          const bCount = b.value.n_posts ?? 0
          const diff =
            sortDirection === 'DESC' ? bCount - aCount : aCount - bCount
          if (diff !== 0) return diff
          return a.displayText.localeCompare(b.displayText)
        })
        return copy
      }
    }
    copy.sort((a, b) => {
      const cmp = a.displayText.localeCompare(b.displayText)
      return sortBy === 'title' && sortDirection === 'DESC' ? -cmp : cmp
    })
    return copy
  }, [
    selectedField,
    precomputedFlattenedValues,
    searchTerm,
    sortBy,
    sortDirection,
  ])

  // Pre-compute a Map of selected values for O(1) lookups
  const selectedValuesMap = useMemo(() => {
    const map = new Map<string, Set<string>>()
    // Build map from all current filter selections
    filteredFilterOptions.forEach((filter) => {
      const selection = getFilterSelection(filter.field)
      if (selection?.values) {
        map.set(filter.field, new Set(selection.values))
      }
    })
    return map
  }, [filteredFilterOptions, getFilterSelection])

  // Determine if this is a brand filter and categorize brands
  const isBrandFilter =
    selectedField === 'brand' || selectedField === 'product_brand'
  const selectedFilter = selectedField
    ? filteredFilterOptions.find((f) => f.field === selectedField)
    : null
  const customerBrands = useMemo(
    () => (selectedFilter as FilterOptionSelect)?.customer_brands || [],
    [selectedFilter],
  )

  // Determine if this is a theme or dataset filter (must be above sort memos
  // that depend on useClusterUuid via isValueSelected)
  const isThemeFilter = selectedField === 'theme'
  const isDatasetFilter = selectedField === 'proj_uuid'
  const useClusterUuid = isThemeFilter || isDatasetFilter

  // A select field the backend shipped with no values at all: its distinct
  // values exceeded the filter-options count limit (ID-like fields such as
  // "Conversation ID"), so there is no list to tick — the pane takes typed
  // exact values instead. Theme/dataset fields keep their own panes.
  const isFreeInputFilter =
    selectedFilter?.filter_type === FilterTypes.SELECT &&
    !useClusterUuid &&
    (selectedFilter.values ?? []).length === 0

  // A numeric custom field (e.g. a price) has no value list either: the pane
  // takes a min/max window. Rating is a window too but keeps its own pane.
  const isRangeFilter =
    selectedFilter?.filter_type === FilterTypes.WINDOW &&
    selectedField !== 'score' &&
    selectedField !== 'post_score'
  const rangeOption = isRangeFilter
    ? (selectedFilter as FilterOptionRange)
    : undefined

  // Derive the active selected-value set for the currently open field.
  // Rebuilt whenever the selection or the active field changes.
  const selectedValuesForField = useMemo(
    () =>
      selectedField ? getFilterSelection(selectedField)?.values || [] : [],
    [selectedField, getFilterSelection],
  )

  // O(1) lookup version of selectedValuesForField — used inside the
  // Virtuoso itemContent renderer so we don't pay an Array.includes
  // scan per rendered row.
  const selectedValuesForFieldSet = useMemo(
    () => new Set(selectedValuesForField),
    [selectedValuesForField],
  )

  // Drives the "Select all" checkbox: true iff every currently-visible
  // (search-filtered) value is already selected. Depends on rightSideContent
  // so it reflects the filtered subset when a search term is active.
  const allVisibleSelected = useMemo<boolean>(() => {
    if (!selectedField || !rightSideContent[0]) return false
    const select = rightSideContent[0] as FilterOptionSelect
    const visibleValues = (select.values || []).map((v) =>
      useClusterUuid ? getSpecialFilterIdentifier(v, selectedField) : v.value,
    )
    if (visibleValues.length === 0) return false
    return visibleValues.every((v) => selectedValuesForFieldSet.has(v))
  }, [
    selectedField,
    rightSideContent,
    useClusterUuid,
    selectedValuesForFieldSet,
  ])

  // Predicate that floats selected values to the top of each list —
  // mirrors FilterPopover.tsx:240-248. Handles de-duped theme/dataset rows
  // (identifiers[]) and the cluster-uuid identifier scheme.
  const isValueSelected = useCallback(
    (value: FilterOptionValue): boolean => {
      if (!selectedField) return false
      if (useClusterUuid && value.identifiers && value.identifiers.length > 0) {
        return value.identifiers.some((id) =>
          selectedValuesForField.includes(id),
        )
      }
      const identifier = useClusterUuid
        ? getSpecialFilterIdentifier(value, selectedField)
        : value.value
      return selectedValuesForField.includes(identifier)
    },
    [selectedField, useClusterUuid, selectedValuesForField],
  )

  const { myBrands, competitorBrands } = useMemo(() => {
    if (!isBrandFilter || !rightSideContent[0])
      return { myBrands: [], competitorBrands: [] }
    const values = (rightSideContent[0] as FilterOptionSelect).values || []
    const isMine = (v: FilterOptionValue): boolean =>
      customerBrands.includes(v.value)
    return {
      myBrands: sortFilterOptionValues(
        values.filter(isMine),
        isValueSelected,
        sortOptions,
      ),
      competitorBrands: sortFilterOptionValues(
        values.filter((v) => !isMine(v)),
        isValueSelected,
        sortOptions,
      ),
    }
  }, [
    isBrandFilter,
    rightSideContent,
    customerBrands,
    isValueSelected,
    sortOptions,
  ])

  const sortedValuesForSelectedField = useMemo<FilterOptionValue[]>(() => {
    const select = rightSideContent[0] as FilterOptionSelect | undefined
    return sortFilterOptionValues(
      select?.values || [],
      isValueSelected,
      sortOptions,
    )
  }, [rightSideContent, isValueSelected, sortOptions])

  // Determine if this is a rating filter (window type)
  const isRatingFilter =
    selectedField === 'score' || selectedField === 'post_score'
  const selectedRatingFilter = rightSideContent[0] as
    | FilterOptionRange
    | undefined
  // "No star rating" rows only exist on the sentence-level `score` field, not
  // the review-level `post_score` rollup.
  //
  // `Null` is a panel-narrowed count, so `stripFilterCounts` strips it and it
  // is `undefined` until this field's counts land — and opening the pane is
  // what fires that fetch. Treating unknown as "hide" made the checkbox appear
  // a round-trip late, and anyone who applied inside that window never got the
  // option at all. Unknown therefore means SHOW, with the pane rendering the
  // number as a placeholder until it lands; a landed 0 still hides it.
  const hasNullScoreReviews =
    selectedField === 'score' &&
    (selectedRatingFilter?.Null === undefined || selectedRatingFilter.Null > 0)

  // The dashboard global's range restriction on the selected field, if any —
  // used both to clamp `ratingCommitted` below (a tile's own PRE-EXISTING
  // rating selection may fall outside a global that changed after it was
  // saved) and to gate `FilterPopoverRatingPane`'s valueType/null-option
  // props further down.
  const ratingRangeRestriction = useMemo(() => {
    if (!selectedField) return undefined
    const fieldRestriction = restriction?.byField.get(selectedField)
    return fieldRestriction?.kind === 'range' ? fieldRestriction : undefined
  }, [restriction, selectedField])

  // Resolve the Rating pane's committed draft inputs from whatever selection
  // shape is on the panel — discrete star values (from the card popover) or
  // an existing window range (min/max/valueType) — into the range shape the
  // pane edits. Null when the field has no filter option to size defaults from.
  const ratingCommitted = useMemo(() => {
    if (!isRatingFilter || !selectedField) return null
    const clampToRestriction = (min: number, max: number): [number, number] => {
      const clampedMin =
        ratingRangeRestriction?.min !== undefined
          ? Math.max(min, ratingRangeRestriction.min)
          : min
      const clampedMax =
        ratingRangeRestriction?.max !== undefined
          ? Math.min(max, ratingRangeRestriction.max)
          : max
      if (clampedMin > clampedMax) {
        const point = ratingRangeRestriction?.min ?? clampedMin
        return [point, point]
      }
      return [clampedMin, clampedMax]
    }
    const existingFilter = getFilterSelection(selectedField)
    if (existingFilter) {
      // If the filter was saved as discrete values from the card popover
      // (e.g. ['1','5']), derive the slider min/max from those so the
      // slider spans the active selection rather than snapping to 1-5.
      const discrete = (existingFilter.values || [])
        .filter((v) => v !== NULL_VALUE)
        .map((v) => Number(v))
        .filter((n) => !Number.isNaN(n))
      const [rawMin, rawMax] =
        discrete.length > 0
          ? [Math.min(...discrete), Math.max(...discrete)]
          : [existingFilter.min ?? 1, existingFilter.max ?? 5]
      const [min, max] = clampToRestriction(rawMin, rawMax)
      return {
        min,
        max,
        valueType: existingFilter.valueType ?? 0,
        includeNull: existingFilter.values?.includes(NULL_VALUE) ?? false,
        hasCommittedFilter: true,
      }
    }
    const selectedFilter = filterOptions.find((f) => f.field === selectedField)
    if (selectedFilter && selectedFilter.filter_type === FilterTypes.WINDOW) {
      const rangeFilter = selectedFilter as FilterOptionRange
      const [min, max] = clampToRestriction(
        rangeFilter.cur_min ?? rangeFilter.min ?? 1,
        rangeFilter.cur_max ?? rangeFilter.max ?? 5,
      )
      return {
        min,
        max,
        valueType: 0,
        includeNull: false,
        hasCommittedFilter: false,
      }
    }
    return null
  }, [
    isRatingFilter,
    selectedField,
    filterOptions,
    getFilterSelection,
    ratingRangeRestriction,
  ])

  const handleRatingApply = (draft: {
    valueType: number
    min: number
    max: number
    includeNull: boolean
  }): void => {
    if (!selectedField) return
    // The Rating pane has its own Apply, so it commits directly even in
    // Apply mode (see `onApplySelections`).
    commitFilterSelect(selectedField, draft.includeNull ? [NULL_VALUE] : [], {
      min: draft.min,
      max: draft.max,
      valueType: draft.valueType,
    })
  }

  const handleRatingClear = (): void => {
    if (!selectedField) return
    commitFilterSelect(selectedField, [])
  }

  // Resolve the Sentiment pane's committed draft inputs. Mirrors
  // `ratingCommitted`, but spans three fields instead of one — see
  // `deriveSentimentMode` for the mode fallback shared with FilterControl's
  // own SentimentFilterCard derivation.
  const sentimentCommitted: SentimentCommitted = useMemo(() => {
    const sentimentSel = getFilterSelection('sentiment')
    const postSentimentSel = getFilterSelection('post_sentiment')
    const numericSel = getFilterSelection('sentiment_filter')
    const mode = deriveSentimentMode(sentimentSel, postSentimentSel)
    return {
      mode,
      values:
        mode === 'record'
          ? (postSentimentSel?.values ?? [])
          : (sentimentSel?.values ?? []),
      numericOperator:
        (numericSel?.operator as NumericOperatorType | undefined) ??
        'greater than',
      numericValue: numericSel?.numericValue,
      hasCommittedFilter:
        sentimentSel !== null ||
        postSentimentSel !== null ||
        numericSel !== null,
    }
  }, [getFilterSelection])

  const sentimentFilterOption = filteredFilterOptions.find(
    (f) => f.field === 'sentiment',
  ) as FilterOptionSelect | undefined

  // Splits the global restriction by mode — 'sentiment' (sentence) and
  // 'post_sentiment' (record) are different fields, so a global constraining
  // one does not constrain the other (see `SentimentPaneRestriction`'s doc
  // comment on why this can't be a single value set).
  const sentimentRestriction = useMemo(():
    | SentimentPaneRestriction
    | undefined => {
    if (!restriction) return undefined
    const sentenceRestriction = restriction.byField.get('sentiment')
    const recordRestriction = restriction.byField.get('post_sentiment')
    const sentence =
      sentenceRestriction?.kind === 'values'
        ? sentenceRestriction.allowed
        : undefined
    const record =
      recordRestriction?.kind === 'values'
        ? recordRestriction.allowed
        : undefined
    if (!sentence && !record) return undefined
    const allowedModes: SentimentMode[] = [
      ...(sentence ? (['sentence'] as const) : []),
      ...(record ? (['record'] as const) : []),
    ]
    return { sentence, record, allowedModes }
  }, [restriction])

  const [themeExpandedState, setThemeExpandedState] = useState<
    Record<string, boolean>
  >({})

  const themesByParent: Record<string, FilterOptionValue[]> =
    (isThemeFilter || isDatasetFilter) && rightSideContent[0]
      ? // De-duplicate theme and dataset values to merge duplicates by display name
        groupThemeValuesByParent(
          deduplicateFilterValues(
            (rightSideContent[0] as FilterOptionSelect).values || [],
          ),
        )
      : {}

  const parentThemes = Object.keys(themesByParent)
  // Bare values file under their own name, so a list with no "Parent: Child"
  // value at all stays a flat list rather than one-child groups.
  const hasThemeHierarchy = Object.values(themesByParent).some((children) =>
    children.some((child) => child.value.includes(': ')),
  )
  const showThemeGroups = isThemeFilter && hasThemeHierarchy

  // True when the active selected-field view renders the plain (non-grouped,
  // non-rating) list — the one branch we virtualize in the field view.
  // Used by the keyboard-nav scroll effect to pick virtuosoRef vs highlightedItemRef.
  const isPlainFieldList =
    selectedField !== null &&
    !isRatingFilter &&
    !isRangeFilter &&
    !(isBrandFilter && (myBrands.length > 0 || competitorBrands.length > 0)) &&
    !showThemeGroups

  // True when every parent theme is expanded (or not yet in the map — default expanded).
  const allParentThemesExpanded =
    parentThemes.length > 0 &&
    parentThemes.every((p) => themeExpandedState[p] ?? true)

  const handleToggleCollapseAllParentThemes = (): void => {
    const next: Record<string, boolean> = {}
    parentThemes.forEach((p) => {
      next[p] = !allParentThemesExpanded
    })
    setThemeExpandedState((prev) => ({ ...prev, ...next }))
  }

  // True when every visible brand group (My Brands / Competitor Brands) is expanded.
  const allBrandGroupsExpanded =
    (myBrands.length > 0 || competitorBrands.length > 0) &&
    (myBrands.length === 0 || myBrandsExpanded) &&
    (competitorBrands.length === 0 || competitorBrandsExpanded)

  const handleToggleCollapseAllBrandGroups = (): void => {
    const next = !allBrandGroupsExpanded
    if (myBrands.length > 0) setMyBrandsExpanded(next)
    if (competitorBrands.length > 0) setCompetitorBrandsExpanded(next)
  }

  const renderSelectAllRow = (
    indented: boolean,
    groupToggle?: { allExpanded: boolean; onToggle: () => void },
  ): React.ReactElement => {
    const totalCount =
      (rightSideContent[0] as FilterOptionSelect | undefined)?.values?.length ??
      0
    return (
      <GroupHeaderRow
        $padding={
          groupToggle ? '4px 8px' : indented ? '4px 8px 4px 36px' : '4px 8px'
        }
        $marginBottom="4px"
        onClick={(event) => {
          event.stopPropagation()
          handleSelectAll()
        }}
      >
        {groupToggle && (
          <ExpandMoreIcon
            style={{
              marginRight: '8px',
              fontSize: '20px',
              color: '#6b7280',
              cursor: 'pointer',
              transition: 'transform 0.2s ease-in-out',
              transform: groupToggle.allExpanded
                ? 'rotate(0deg)'
                : 'rotate(-90deg)',
              flexShrink: 0,
            }}
            onClick={(e) => {
              e.stopPropagation()
              groupToggle.onToggle()
            }}
          />
        )}
        <SmallCheckbox
          checked={allVisibleSelected}
          onChange={() => {}}
          style={{ marginRight: '8px' }}
        />
        <span
          style={{
            color: '#1f2937',
            fontWeight: '600',
            fontSize: '13px',
          }}
        >
          Select all{' '}
          <span
            style={{ fontWeight: '400', color: '#6b7280', fontSize: '12px' }}
          >
            ({numberFormatter.format(totalCount)})
          </span>
        </span>
      </GroupHeaderRow>
    )
  }

  // Update master checkbox states based on selected values
  useEffect(() => {
    if (selectedField && isBrandFilter) {
      const existingFilter = getFilterSelection(selectedField)
      const selectedValues = existingFilter?.values || []

      if (myBrands.length > 0) {
        const allMyBrandsSelected = myBrands.every((b) =>
          selectedValues.includes(b.value),
        )
        setSelectMyBrands(allMyBrandsSelected)
      }

      if (competitorBrands.length > 0) {
        const allCompetitorBrandsSelected = competitorBrands.every((b) =>
          selectedValues.includes(b.value),
        )
        setSelectCompetitorBrands(allCompetitorBrandsSelected)
      }
    }
  }, [
    selectedField,
    isBrandFilter,
    myBrands,
    competitorBrands,
    getFilterSelection,
  ])

  // Initialize theme expanded state (all expanded by default)
  useEffect(() => {
    if (showThemeGroups) {
      const initialState: Record<string, boolean> = {}
      parentThemes.forEach((parent) => {
        if (!(parent in themeExpandedState)) {
          initialState[parent] = true
        }
      })
      if (Object.keys(initialState).length > 0) {
        setThemeExpandedState((prev) => ({ ...prev, ...initialState }))
      }
    }
  }, [showThemeGroups, parentThemes, themeExpandedState])

  // Handle My Brands selection
  const handleSelectMyBrands = (): void => {
    if (!selectedField) return

    const myBrandValues = myBrands.map((b) => b.value)
    const existingFilter = getFilterSelection(selectedField)
    const currentValues = existingFilter?.values || []

    let newSelectedValues: string[]
    if (selectMyBrands) {
      // Uncheck all my brands
      newSelectedValues = currentValues.filter(
        (v) => !myBrandValues.includes(v),
      )
    } else {
      // Check all my brands
      newSelectedValues = [
        ...currentValues.filter((v) => !myBrandValues.includes(v)),
        ...myBrandValues,
      ]
    }

    onFilterSelect(selectedField, newSelectedValues)
  }

  // Handle Competitor Brands selection
  const handleSelectCompetitorBrands = (): void => {
    if (!selectedField) return

    const competitorBrandValues = competitorBrands.map((b) => b.value)
    const existingFilter = getFilterSelection(selectedField)
    const currentValues = existingFilter?.values || []

    let newSelectedValues: string[]
    if (selectCompetitorBrands) {
      // Uncheck all competitor brands
      newSelectedValues = currentValues.filter(
        (v) => !competitorBrandValues.includes(v),
      )
    } else {
      // Check all competitor brands
      newSelectedValues = [
        ...currentValues.filter((v) => !competitorBrandValues.includes(v)),
        ...competitorBrandValues,
      ]
    }

    onFilterSelect(selectedField, newSelectedValues)
  }

  // Handle Parent Theme selection (select/deselect all child themes)
  const handleSelectParentTheme = (parentTheme: string): void => {
    if (!selectedField) return

    const childThemes = themesByParent[parentTheme] || []
    // Collect all identifiers from de-duped child themes
    const childThemeIdentifiers: string[] = []
    childThemes.forEach((t) => {
      if (t.identifiers && t.identifiers.length > 0) {
        childThemeIdentifiers.push(...t.identifiers)
      } else {
        childThemeIdentifiers.push(getSpecialFilterIdentifier(t, selectedField))
      }
    })
    const existingFilter = getFilterSelection(selectedField)
    const currentValues = existingFilter?.values || []

    // Check if all child themes are currently selected (by their identifiers)
    const allChildrenSelected = childThemeIdentifiers.every((id) =>
      currentValues.includes(id),
    )

    let newSelectedValues: string[]
    if (allChildrenSelected) {
      // Uncheck all child themes
      newSelectedValues = currentValues.filter(
        (v) => !childThemeIdentifiers.includes(v),
      )
    } else {
      // Check all child themes
      newSelectedValues = [
        ...currentValues.filter((v) => !childThemeIdentifiers.includes(v)),
        ...childThemeIdentifiers,
      ]
    }

    onFilterSelect(selectedField, newSelectedValues)
  }

  // Check if all children of a parent theme are selected
  const isParentThemeSelected = (parentTheme: string): boolean => {
    if (!selectedField) return false

    const childThemes = themesByParent[parentTheme] || []
    const existingFilter = getFilterSelection(selectedField)
    const selectedValues = existingFilter?.values || []

    // Check all identifiers from de-duped children
    return (
      childThemes.length > 0 &&
      childThemes.every((t) => {
        if (t.identifiers && t.identifiers.length > 0) {
          // For de-duped rows, check if ALL identifiers are selected
          return t.identifiers.every((id) => selectedValues.includes(id))
        }
        return selectedValues.includes(
          getSpecialFilterIdentifier(t, selectedField),
        )
      })
    )
  }

  // Handle "only" click - select only the specified value
  // For theme and dataset filters, pass the FilterOptionValue to use cluster_uuid
  const handleOnlyClick = (
    value: string | FilterOptionValue,
    useClusterUuid: boolean = false,
  ): void => {
    if (!selectedField) return

    // Get the identifier to store (cluster_uuid for themes/datasets, value otherwise)
    const identifier =
      useClusterUuid && typeof value !== 'string'
        ? getSpecialFilterIdentifier(value, selectedField)
        : typeof value === 'string'
          ? value
          : value.value

    const newSelectedValues = [identifier]

    // Update the global filter state
    onFilterSelect(selectedField, newSelectedValues)
  }

  // Calculate smart position based on trigger element
  const [layout, setLayout] = useState<PopoverLayout>({
    top: 0,
    left: 0,
    width: POPOVER_MAX_WIDTH,
    height: POPOVER_MAX_HEIGHT,
  })
  const [isPositionCalculated, setIsPositionCalculated] = useState(false)

  // Sized and placed from the viewport, and re-fitted when it changes, so the
  // popover is always fully on screen — see `getPopoverLayout`.
  useEffect(() => {
    if (!triggerElement) return
    const place = (): void => {
      setLayout(
        getPopoverLayout({
          trigger: triggerElement.getBoundingClientRect(),
          anchorMode,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
        }),
      )
      setIsPositionCalculated(true)
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [triggerElement, anchorMode])

  type FlattenedItem = {
    parentField: string
    parentAlias: string
    value: FilterOptionValue
    displayText: string
    kind?: 'parent'
  }

  // Whether a flattened "All" row renders checked. A parent row (a theme
  // parent, or a My / Competitor brand or product group) needs EVERY child
  // selected (`isParentThemeSelected`'s rule); a de-duped theme row is checked
  // when any of its uuids is.
  const isFlatItemChecked = (item: FlattenedItem): boolean => {
    const selectedSet = selectedValuesMap.get(item.parentField)
    const isThemeOrDataset =
      item.parentField === 'theme' || item.parentField === 'proj_uuid'
    if (
      (isThemeOrDataset || item.kind === 'parent') &&
      item.value.identifiers &&
      item.value.identifiers.length > 0
    ) {
      const has = (id: string): boolean => selectedSet?.has(id) ?? false
      return item.kind === 'parent'
        ? item.value.identifiers.every(has)
        : item.value.identifiers.some(has)
    }
    return (
      selectedSet?.has(
        isThemeOrDataset
          ? getSpecialFilterIdentifier(item.value, item.parentField)
          : item.value.value,
      ) ?? false
    )
  }

  // The next selection for a flattened "All" row, shared by the click and
  // Enter handlers. A parent row is all-or-nothing (the Theme view's
  // `handleSelectParentTheme` rule); a de-duped theme row toggles on ANY of
  // its cluster uuids.
  const toggleFlatItem = (item: FlattenedItem): void => {
    const currentValues = Array.from(
      selectedValuesMap.get(item.parentField) ?? [],
    )
    const isThemeOrDataset =
      item.parentField === 'theme' || item.parentField === 'proj_uuid'

    let newSelectedValues: string[]
    if (
      (isThemeOrDataset || item.kind === 'parent') &&
      item.value.identifiers &&
      item.value.identifiers.length > 0
    ) {
      const allIds = item.value.identifiers
      const toggleOff =
        item.kind === 'parent'
          ? allIds.every((id) => currentValues.includes(id))
          : allIds.some((id) => currentValues.includes(id))
      newSelectedValues = toggleOff
        ? currentValues.filter((v) => !allIds.includes(v))
        : [
            ...currentValues,
            ...allIds.filter((id) => !currentValues.includes(id)),
          ]
    } else {
      const identifier = isThemeOrDataset
        ? getSpecialFilterIdentifier(item.value, item.parentField)
        : item.value.value
      newSelectedValues = currentValues.includes(identifier)
        ? currentValues.filter((v) => v !== identifier)
        : [...currentValues, identifier]
    }
    onFilterSelect(item.parentField, newSelectedValues)
  }

  // Get navigable items (must share order with the rendered list — see sortedValuesForSelectedField).
  const getNavigableItems = ():
    | FilterOptionValue[]
    | FilterOption[]
    | FlattenedItem[]
    | SavedFilterPreset[] => {
    if (view === 'saved') {
      const list = savedPresets || []
      return searchTerm
        ? list.filter((p) => matchesSearchTerms(p.name, searchTerm))
        : list
    }
    if (pane !== null) {
      // A special pane (Sentiment/Keywords/Smart search/Records/Sentences)
      // is open — it has no field selected, so without this guard the
      // fallback below would let Enter-on-search-input toggle an unrelated
      // filter from the flattened "All" list underneath the pane.
      return []
    }
    if (selectedField && rightSideContent[0]) {
      return sortedValuesForSelectedField
    } else {
      // When in "All" view, navigate through flattened child values
      return flattenedChildValues
    }
  }

  const navigableItems = getNavigableItems()

  // Counts arrive per expanded field, AFTER the list has already rendered, and
  // `sortFilterOptionValues` degrades a count sort to alphabetical until every
  // `n_posts` is known — so the landing flips the whole list from A-Z into
  // count order. `_merge_select_options` zero-fills unmatched values in place
  // instead of dropping them, so the length is invariant across that flip: a
  // length-keyed re-anchor never fires and `highlightedIndex` keeps pointing at
  // a position now holding a different value, which Enter would then toggle.
  // Tracking the same predicate the sort keys on is what catches the flip.
  //
  // Memoized because the RECURRING case is counts-known, where `every` walks
  // the whole list — and this recomputes on every render, which means every
  // search keystroke and every arrow key. Both inputs are already memoized, so
  // this costs nothing between their changes.
  const navCountsKnown = useMemo(
    () =>
      selectedField
        ? sortedValuesForSelectedField.every((v) => v.n_posts !== undefined)
        : flattenedChildValues.every(
            (item) => item.value.n_posts !== undefined,
          ),
    [selectedField, sortedValuesForSelectedField, flattenedChildValues],
  )

  // Remember WHICH item the user aimed at, so a re-sort can move the highlight
  // with it. Written only where the user moves the highlight (the arrow keys) and
  // where we re-anchor below — never derived from `navigableItems[highlighted]`
  // during render, which is already the wrong item once the list has flipped.
  //
  // The arrow-key writes sit inside the `setHighlightedIndex` updater, which
  // makes those updaters impure — deliberate, and safe: StrictMode's double
  // invocation recomputes the same `next` from the same `prev` and writes the
  // same key, so it is idempotent, and nothing derives state from the ref. Kept
  // in the updater rather than hoisted because computing `next` from the render's
  // `highlightedIndex` instead of `prev` would go stale if two arrow presses
  // batched into one commit.
  const highlightedKeyRef = useRef<string | null>(null)

  // Re-anchor when the content changes. Resetting to the head would be safe but
  // lossy, and lossy every time: toggling a value changes `panel.filters`, which
  // changes the counts query key, which drops `data` to `undefined` (no
  // `keepPreviousData`) and flips `navCountsKnown` back to false — so
  // arrow-down-toggle-arrow-down would bounce home after every toggle. Find the
  // remembered item in the new order instead, and fall back to the head only
  // when it is genuinely gone (field switch, search change).
  // The list itself is reached through a ref rather than a dependency: it is a
  // fresh array on every render, so depending on it would re-anchor constantly.
  // The keys below stand in for it — every content change that can move an
  // item's index: the field or search term changing the list outright, its
  // length changing, counts landing (which flips the sort from alphabetical to
  // count order), and the user picking a different sort, which reorders with the
  // length and the counts-known state both unchanged. Written in its own effect
  // declared FIRST, so it is already current when the re-anchor effect runs in
  // the same commit.
  const navigableItemsRef = useRef(navigableItems)
  useEffect(() => {
    navigableItemsRef.current = navigableItems
  })

  useEffect(() => {
    const items = navigableItemsRef.current
    const remembered = highlightedKeyRef.current
    const nextIndex = remembered
      ? items.findIndex((item) => navigableItemKey(item) === remembered)
      : -1
    const resolved = nextIndex >= 0 ? nextIndex : 0
    highlightedKeyRef.current = navigableItemKey(items[resolved])
    setHighlightedIndex(resolved)
  }, [
    selectedField,
    searchTerm,
    navigableItems.length,
    navCountsKnown,
    effectiveSortKey,
  ])

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0) {
      if ((!selectedField || isPlainFieldList) && virtuosoRef.current) {
        // For virtualized lists: the "All" view (!selectedField) and the
        // plain field list (isPlainFieldList). Both use Virtuoso's
        // scrollToIndex so the highlighted row is always in the viewport.
        virtuosoRef.current.scrollToIndex({
          index: highlightedIndex,
          behavior: 'auto',
          align: 'center',
        })
      } else if (highlightedItemRef.current) {
        // For non-virtualized lists (grouped brand/theme, saved filters,
        // and rating slider views).
        highlightedItemRef.current.scrollIntoView({
          behavior: 'auto',
          block: 'nearest',
          inline: 'nearest',
        })
      }
    }
  }, [highlightedIndex, selectedField, isPlainFieldList])

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (navigableItems.length === 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setHighlightedIndex((prev) => {
          const next = prev < navigableItems.length - 1 ? prev + 1 : prev
          highlightedKeyRef.current = navigableItemKey(navigableItems[next])
          return next
        })
        break
      case 'ArrowUp':
        event.preventDefault()
        setHighlightedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : prev
          highlightedKeyRef.current = navigableItemKey(navigableItems[next])
          return next
        })
        break
      case 'Enter':
        event.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < navigableItems.length) {
          const item = navigableItems[highlightedIndex]
          if (view === 'saved') {
            applySavedPreset(item as SavedFilterPreset)
            onClose()
          } else if (selectedField && rightSideContent[0]) {
            // Toggle checkbox for the value
            const value = item as FilterOptionValue
            // For theme or dataset filters, pass the full value to use cluster_uuid
            handleValueToggle(value, useClusterUuid)
          } else {
            // Selecting from flattened list in "All" view - apply filter directly
            toggleFlatItem(item as unknown as FlattenedItem)
          }
        }
        break
      // Escape is handled by useEscapeDismiss above — it needs to work
      // regardless of whether this popover holds DOM focus, so it isn't
      // duplicated here.
    }
  }

  const handleLeftSideClick = (
    item: (typeof leftSidebarItems)[0],
    event: React.MouseEvent,
  ): void => {
    event.stopPropagation() // Prevent event bubbling that might close the modal
    if (item.type === 'group') {
      // A subject header is a label, not a destination
      return
    } else if (getSidebarItemDisabledReason(item) !== null) {
      // Mutually-exclusive search function already applied, or a field this
      // picker was told it can't offer — ignore clicks.
      return
    }
    // Clear the search box when switching tabs so the new tab starts unfiltered.
    setSearchTerm('')
    if (item.type === 'saved') {
      setView('saved')
      setSelectedField(null)
      setPane(null)
    } else if (item.type === 'category') {
      setView('fields')
      setSelectedField(null)
      setPane(null)
    } else if (item.type === 'field') {
      setView('fields')
      setSelectedField(item.field)
      setPane(null)
    } else if (item.type === 'keywords') {
      // Open the Keywords pane — does NOT auto-insert a card
      setView('fields')
      setSelectedField(null)
      setPane('keywords')
    } else if (item.type === 'smart_search') {
      // Open the Smart search pane — does NOT auto-insert a card
      setView('fields')
      setSelectedField(null)
      setPane('smart_search')
    } else if (item.type === 'numeric') {
      // Open the Records/Sentences pane — does NOT auto-insert a card
      setView('fields')
      setSelectedField(null)
      setPane(item.numericField)
    } else if (item.type === 'sentiment') {
      // Open the sentiment pane — does NOT auto-insert a card
      setView('fields')
      setSelectedField(null)
      setPane('sentiment')
    }
    // None of the branches above close the popover — Apply/Clear inside each
    // pane are the only things that write to the panel.

    // Re-focus the search input after selecting a category
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus()
      }
    }, 0)
  }

  // Defaults to document.body — a position: fixed overlay needs the root
  // stacking context to out-stack whatever the caller's ancestor tree does
  // (a `position: sticky` ancestor creates its own stacking context
  // unconditionally, which silently traps a fixed zIndex inside it; see
  // popoverPortalTarget's doc comment above). Matches every sibling popover
  // in charts-v2 (FilterPopover, FilterOperatorPopover, SimpleDropdown, …).
  const portalContainer = popoverPortalTarget ?? document.body

  return createPortal(
    <>
      {/* Filter Selection Popover */}
      <div
        data-yogi-overlay="true"
        style={{
          position: 'fixed',
          top: layout.top,
          left: layout.left,
          width: layout.width,
          height: layout.height,
          backgroundColor: 'white',
          border: '1px solid #d8d8d8',
          borderRadius: '6px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          opacity: isPositionCalculated ? 1 : 0,
          transition: 'opacity 0.1s ease-in',
        }}
        // The popover renders through a portal, but React synthetic events
        // bubble through the React tree — clicks on text/headers reach the
        // trigger's onClick and toggle the popover shut. Stop bubbling once
        // at the root so individual sites don't each need stopPropagation.
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Search Bar */}
        <div
          style={{ padding: '12px', borderBottom: `1px solid ${gray[200]}` }}
        >
          <SearchInput
            ref={searchInputRef}
            $size="sm"
            type="text"
            placeholder="Search filters..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left Sidebar - Categories */}
          <div
            style={{
              width: '160px',
              flexShrink: 0,
              borderRight: `1px solid ${gray[200]}`,
              borderBottomLeftRadius: '6px',
              backgroundColor: gray[50],
              overflow: 'auto',
            }}
          >
            {leftSidebarItems.map((item) => {
              // A group header ("Source") and a field row ("Source") can share
              // a name, so the key carries the item's type / field id.
              const itemKey =
                item.type === 'field'
                  ? `field:${item.field}`
                  : `${item.type}:${item.name}`

              const isSelected = isSidebarItemSelected(item, {
                view,
                selectedField,
                pane,
              })

              const disabledReason = getSidebarItemDisabledReason(item)
              const isDisabled = disabledReason !== null

              // Disabled reason wins over the description so the user sees why
              // the item can't be clicked; otherwise show its description.
              const tooltipTitle = disabledReason ?? item.description

              const itemNode =
                item.type === 'group' ? (
                  <SidebarGroupHeader>{item.name}</SidebarGroupHeader>
                ) : (
                  <SidebarItem
                    $selected={isSelected}
                    $disabled={isDisabled}
                    $bold={item.type === 'category'}
                    onClick={(event) => handleLeftSideClick(item, event)}
                  >
                    {item.glyph && (
                      <SidebarItemGlyph aria-hidden>
                        {item.glyph}
                      </SidebarItemGlyph>
                    )}
                    {item.name}
                  </SidebarItem>
                )

              return (
                <Tooltip
                  key={itemKey}
                  content={tooltipTitle}
                  side="right"
                  // Popover panel sits at zIndex 2000; the tooltip's default
                  // zIndex (~1070) renders behind it, so the tooltip is
                  // invisible. Lift above the panel (matches the Dropdown's
                  // zIndex 2001 elsewhere in this file).
                  zIndex={2001}
                >
                  {itemNode}
                </Tooltip>
              )
            })}
          </div>

          {/* Right Content - Filter Options */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <div
              style={{
                padding: '12px',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'auto',
              }}
            >
              {view === 'saved' ? (
                // Saved filters pane
                <div>
                  <h3
                    style={{
                      margin: '0 0 8px 0',
                      fontSize: '14px',
                      fontWeight: '600',
                    }}
                  >
                    Saved filters
                    <span
                      style={{
                        fontWeight: '400',
                        color: '#6b7280',
                        marginLeft: '8px',
                      }}
                    >
                      (
                      {(() => {
                        const list = savedPresets || []
                        const filtered = searchTerm
                          ? list.filter((p) =>
                              matchesSearchTerms(p.name, searchTerm),
                            )
                          : list
                        return numberFormatter.format(filtered.length)
                      })()}
                      )
                    </span>
                  </h3>

                  {savedPresetsLoading ? (
                    <div
                      style={{
                        padding: '8px',
                        color: '#6b7280',
                        fontSize: '13px',
                      }}
                    >
                      Loading…
                    </div>
                  ) : (
                    (() => {
                      const list = savedPresets || []
                      const filtered = searchTerm
                        ? list.filter((p) =>
                            matchesSearchTerms(p.name, searchTerm),
                          )
                        : list

                      if (filtered.length === 0) {
                        return (
                          <div
                            style={{
                              padding: '8px',
                              color: '#6b7280',
                              fontSize: '13px',
                            }}
                          >
                            {searchTerm
                              ? 'No saved filters match your search.'
                              : 'No saved filters yet — save one from the chart toolbar.'}
                          </div>
                        )
                      }

                      return filtered.map((preset, index) => {
                        const isHighlighted = highlightedIndex === index

                        return (
                          <HoverableValueRow
                            key={preset.name}
                            ref={isHighlighted ? highlightedItemRef : null}
                            $active={isHighlighted}
                            style={{
                              padding: '6px 8px',
                              marginBottom: '4px',
                            }}
                            onClick={(e) => {
                              e.stopPropagation()
                              // The Popconfirm popup is portalled, but its
                              // clicks still bubble here through React.
                              if (!e.currentTarget.contains(e.target as Node))
                                return
                              applySavedPreset(preset)
                              onClose()
                            }}
                          >
                            <span
                              style={{ fontSize: '13px', color: '#1f2937' }}
                            >
                              {preset.name}
                            </span>
                            <Popconfirm
                              title="Are you sure you want to delete this saved filter?"
                              okText="Yes"
                              cancelText="No"
                              okButtonProps={{ danger: true }}
                              // Popover's outside-click overlay sits at zIndex
                              // 1999; the Popconfirm's default (1060) would
                              // render behind it.
                              zIndex={2001}
                              onConfirm={() => {
                                filterService
                                  .deleteFilterPreset(projectId, preset.name)
                                  .then(() => refetchSavedPresets())
                              }}
                            >
                              <PaneRowDeleteButton
                                className="row-action-btn"
                                onClick={(e) => e.stopPropagation()}
                                title="Delete saved filter"
                              >
                                <DeleteOutlineIcon style={{ fontSize: 16 }} />
                              </PaneRowDeleteButton>
                            </Popconfirm>
                          </HoverableValueRow>
                        )
                      })
                    })()
                  )}
                </div>
              ) : isSentimentPane ? (
                <FilterPopoverSentimentPane
                  sentimentFilterOption={sentimentFilterOption}
                  sortOptions={sortOptions}
                  committed={sentimentCommitted}
                  onApply={(draft) => onSentimentApply?.(draft)}
                  onClear={() => onSentimentClear?.()}
                  restriction={sentimentRestriction}
                />
              ) : pane === 'keywords' ? (
                <FilterPopoverKeywordsPane
                  keywordFilters={keywordFilters}
                  onApply={(filters) => onKeywordFiltersApply?.(filters)}
                  onClear={() => onKeywordFiltersClear?.()}
                />
              ) : pane === 'smart_search' ? (
                <FilterPopoverSmartSearchPane
                  smartSearchValue={smartSearchValue}
                  onApply={(value) => onSmartSearchApply?.(value)}
                  onClear={() => onSmartSearchClear?.()}
                />
              ) : pane === 'record_count' || pane === 'sentence_count' ? (
                <FilterPopoverNumericPane
                  field={pane}
                  label={NUMERIC_FILTER_LABELS[pane]}
                  operator={
                    (getFilterSelection(pane)?.operator as
                      | NumericOperatorType
                      | undefined) ?? null
                  }
                  value={getFilterSelection(pane)?.numericValue}
                  onApply={(operator, value) =>
                    onNumericFilterApply?.(pane, operator, value)
                  }
                  onClear={() => onNumericFilterClear?.(pane)}
                />
              ) : selectedField ? (
                // Show values for the selected field
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      margin: '0 0 8px 0',
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        fontSize: '14px',
                        fontWeight: '600',
                      }}
                    >
                      {rightSideContent[0]?.alias || rightSideContent[0]?.field}
                      {rightSideContent[0]?.filter_type === 'select' &&
                        !isFreeInputFilter && (
                          <span
                            style={{
                              fontWeight: '400',
                              color: '#6b7280',
                              marginLeft: '8px',
                            }}
                          >
                            (
                            {numberFormatter.format(
                              (rightSideContent[0] as FilterOptionSelect).values
                                ?.length || 0,
                            )}
                            )
                          </span>
                        )}
                    </h3>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      {!isRatingFilter &&
                        !isRangeFilter &&
                        !isFreeInputFilter &&
                        sortDropdown}
                    </div>
                  </div>

                  {isRatingFilter && rightSideContent[0] && ratingCommitted ? (
                    <FilterPopoverRatingPane
                      rangeFilter={selectedRatingFilter as FilterOptionRange}
                      showNullOption={
                        hasNullScoreReviews &&
                        (ratingRangeRestriction?.allowNull ?? true)
                      }
                      nullOptionLabel={
                        selectedField === 'score'
                          ? 'Reviews with no star rating'
                          : ALIAS_FOR_NULL_VALUES
                      }
                      allowedValueTypes={
                        ratingRangeRestriction?.allowedValueTypes
                      }
                      committed={ratingCommitted}
                      onApply={handleRatingApply}
                      onClear={handleRatingClear}
                    />
                  ) : rangeOption && selectedField ? (
                    <FilterPopoverRangePane
                      key={selectedField}
                      bounds={{
                        min: rangeOption.cur_min ?? rangeOption.min,
                        max: rangeOption.cur_max ?? rangeOption.max,
                      }}
                      committed={{
                        min: getFilterSelection(selectedField)?.min,
                        max: getFilterSelection(selectedField)?.max,
                      }}
                      onApply={(range) =>
                        commitFilterSelect(selectedField, [], range)
                      }
                      onClear={() => commitFilterSelect(selectedField, [])}
                    />
                  ) : isFreeInputFilter && selectedField ? (
                    <FilterPopoverFreeInputPane
                      key={selectedField}
                      committedValues={selectedValuesForField}
                      onApply={(values) =>
                        commitFilterSelect(selectedField, values)
                      }
                      onClear={() => commitFilterSelect(selectedField, [])}
                    />
                  ) : isBrandFilter &&
                    (myBrands.length > 0 || competitorBrands.length > 0) ? (
                    /* Brand grouping section - Collapsible nested lists */
                    <>
                      {renderSelectAllRow(true, {
                        allExpanded: allBrandGroupsExpanded,
                        onToggle: handleToggleCollapseAllBrandGroups,
                      })}
                      {/* Selected Values Display */}
                      {(() => {
                        const existingFilter = getFilterSelection(selectedField)
                        const selectedValues = existingFilter?.values || []
                        return selectedValues.length > 0 ? (
                          <div
                            style={{
                              backgroundColor: '#f3f4f6',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              marginBottom: '12px',
                              fontSize: '13px',
                            }}
                          >
                            <span style={{ color: '#6b7280' }}>Selected: </span>
                            <span
                              style={{
                                color: accents.reflectionBlue[500],
                                fontWeight: '500',
                              }}
                            >
                              {numberFormatter.format(selectedValues.length)}{' '}
                              value
                              {selectedValues.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        ) : null
                      })()}

                      {/* My Brands Group */}
                      {myBrands.length > 0 && (
                        <div style={{ marginBottom: '4px' }}>
                          {/* My Brands Header */}
                          <GroupHeaderRow
                            $padding="4px 8px"
                            $marginBottom="2px"
                          >
                            {/* Expand/Collapse Arrow */}
                            <ExpandMoreIcon
                              style={{
                                marginRight: '8px',
                                fontSize: '20px',
                                color: '#6b7280',
                                cursor: 'pointer',
                                transition: 'transform 0.2s ease-in-out',
                                transform: myBrandsExpanded
                                  ? 'rotate(0deg)'
                                  : 'rotate(-90deg)',
                              }}
                              onClick={(e) => {
                                e.stopPropagation()
                                setMyBrandsExpanded(!myBrandsExpanded)
                              }}
                            />

                            {/* Checkbox */}
                            <SmallCheckbox
                              checked={selectMyBrands}
                              onChange={handleSelectMyBrands}
                              onClick={(e) => e.stopPropagation()}
                              style={{ marginRight: '8px' }}
                            />

                            {/* Label */}
                            <span
                              style={{
                                color: '#1f2937',
                                fontWeight: '500',
                                fontSize: '13px',
                                flex: 1,
                              }}
                              onClick={() =>
                                setMyBrandsExpanded(!myBrandsExpanded)
                              }
                            >
                              My Brands{' '}
                              <span
                                style={{
                                  fontWeight: '400',
                                  color: '#6b7280',
                                  fontSize: '12px',
                                }}
                              >
                                ({numberFormatter.format(myBrands.length)})
                              </span>
                            </span>
                          </GroupHeaderRow>

                          {/* My Brands Children */}
                          {myBrandsExpanded &&
                            myBrands.map(
                              (value: FilterOptionValue, index: number) => {
                                const existingFilter =
                                  getFilterSelection(selectedField)
                                const selectedValues =
                                  existingFilter?.values || []
                                const isChecked = selectedValues.includes(
                                  useClusterUuid
                                    ? getSpecialFilterIdentifier(
                                        value,
                                        selectedField,
                                      )
                                    : value.value,
                                )

                                return (
                                  <HoverableValueRow
                                    key={`my-brand-${value.value}-${index}`}
                                    $active={isChecked}
                                    style={{
                                      padding: '4px 8px 4px 36px', // Indented
                                      marginBottom: '2px',
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleValueToggle(
                                        useClusterUuid ? value : value.value,
                                        useClusterUuid,
                                      )
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <SmallCheckbox
                                        checked={isChecked}
                                        onChange={() => {}}
                                        style={{ marginRight: '12px' }}
                                      />
                                      <span
                                        style={{
                                          fontSize: '13px',
                                          color: '#1f2937',
                                        }}
                                      >
                                        {isNullValueAlias(value.value)
                                          ? ALIAS_FOR_NULL_VALUES
                                          : value.value}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                      }}
                                    >
                                      <PaneRowOnlyButton
                                        className="row-action-btn"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleOnlyClick(
                                            useClusterUuid
                                              ? value
                                              : value.value,
                                            useClusterUuid,
                                          )
                                        }}
                                      >
                                        only
                                      </PaneRowOnlyButton>
                                      <FilterOptionPostCountCell
                                        nPosts={value.n_posts}
                                      />
                                    </div>
                                  </HoverableValueRow>
                                )
                              },
                            )}
                        </div>
                      )}

                      {/* Competitor Brands Group */}
                      {competitorBrands.length > 0 && (
                        <div style={{ marginBottom: '4px' }}>
                          {/* Competitor Brands Header */}
                          <GroupHeaderRow
                            $padding="4px 8px"
                            $marginBottom="2px"
                          >
                            {/* Expand/Collapse Arrow */}
                            <ExpandMoreIcon
                              style={{
                                marginRight: '8px',
                                fontSize: '20px',
                                color: '#6b7280',
                                cursor: 'pointer',
                                transition: 'transform 0.2s ease-in-out',
                                transform: competitorBrandsExpanded
                                  ? 'rotate(0deg)'
                                  : 'rotate(-90deg)',
                              }}
                              onClick={(e) => {
                                e.stopPropagation()
                                setCompetitorBrandsExpanded(
                                  !competitorBrandsExpanded,
                                )
                              }}
                            />

                            {/* Checkbox */}
                            <SmallCheckbox
                              checked={selectCompetitorBrands}
                              onChange={handleSelectCompetitorBrands}
                              onClick={(e) => e.stopPropagation()}
                              style={{ marginRight: '8px' }}
                            />

                            {/* Label */}
                            <span
                              style={{
                                color: '#1f2937',
                                fontWeight: '500',
                                fontSize: '13px',
                                flex: 1,
                              }}
                              onClick={() =>
                                setCompetitorBrandsExpanded(
                                  !competitorBrandsExpanded,
                                )
                              }
                            >
                              Competitor Brands{' '}
                              <span
                                style={{
                                  fontWeight: '400',
                                  color: '#6b7280',
                                  fontSize: '12px',
                                }}
                              >
                                (
                                {numberFormatter.format(
                                  competitorBrands.length,
                                )}
                                )
                              </span>
                            </span>
                          </GroupHeaderRow>

                          {/* Competitor Brands Children */}
                          {competitorBrandsExpanded &&
                            competitorBrands.map(
                              (value: FilterOptionValue, index: number) => {
                                const existingFilter =
                                  getFilterSelection(selectedField)
                                const selectedValues =
                                  existingFilter?.values || []
                                const isChecked = selectedValues.includes(
                                  useClusterUuid
                                    ? getSpecialFilterIdentifier(
                                        value,
                                        selectedField,
                                      )
                                    : value.value,
                                )

                                return (
                                  <HoverableValueRow
                                    key={`competitor-brand-${value.value}-${index}`}
                                    $active={isChecked}
                                    style={{
                                      padding: '4px 8px 4px 36px', // Indented
                                      marginBottom: '2px',
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleValueToggle(
                                        useClusterUuid ? value : value.value,
                                        useClusterUuid,
                                      )
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <SmallCheckbox
                                        checked={isChecked}
                                        onChange={() => {}}
                                        style={{ marginRight: '12px' }}
                                      />
                                      <span
                                        style={{
                                          fontSize: '13px',
                                          color: '#1f2937',
                                        }}
                                      >
                                        {isNullValueAlias(value.value)
                                          ? ALIAS_FOR_NULL_VALUES
                                          : value.value}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                      }}
                                    >
                                      <PaneRowOnlyButton
                                        className="row-action-btn"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleOnlyClick(
                                            useClusterUuid
                                              ? value
                                              : value.value,
                                            useClusterUuid,
                                          )
                                        }}
                                      >
                                        only
                                      </PaneRowOnlyButton>
                                      <FilterOptionPostCountCell
                                        nPosts={value.n_posts}
                                      />
                                    </div>
                                  </HoverableValueRow>
                                )
                              },
                            )}
                        </div>
                      )}
                    </>
                  ) : showThemeGroups ? (
                    <>
                      {/* Theme grouping section - Collapsible nested lists by parent theme */}
                      {renderSelectAllRow(true, {
                        allExpanded: allParentThemesExpanded,
                        onToggle: handleToggleCollapseAllParentThemes,
                      })}
                      {/* Selected Values Display */}
                      {(() => {
                        const existingFilter = getFilterSelection(selectedField)
                        const selectedUUIDs = existingFilter?.values || []

                        // Count unique theme display names (de-duplicated count)
                        const uniqueThemeNames = new Set<string>()
                        Object.values(themesByParent).forEach((themes) => {
                          themes.forEach((theme) => {
                            if (
                              theme.identifiers &&
                              theme.identifiers.length > 0
                            ) {
                              // Check if ANY of the identifiers are selected
                              const isSelected = theme.identifiers.some((id) =>
                                selectedUUIDs.includes(id),
                              )
                              if (isSelected) {
                                uniqueThemeNames.add(theme.value)
                              }
                            }
                          })
                        })

                        const selectedCount = uniqueThemeNames.size
                        return selectedCount > 0 ? (
                          <div
                            style={{
                              backgroundColor: '#f3f4f6',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              marginBottom: '12px',
                              fontSize: '13px',
                            }}
                          >
                            <span style={{ color: '#6b7280' }}>Selected: </span>
                            <span
                              style={{
                                color: accents.reflectionBlue[500],
                                fontWeight: '500',
                              }}
                            >
                              {selectedCount} value
                              {selectedCount !== 1 ? 's' : ''}
                            </span>
                          </div>
                        ) : null
                      })()}

                      {/* Render each parent theme as a collapsible group */}
                      {parentThemes.map((parentTheme) => {
                        const childThemes = sortFilterOptionValues(
                          themesByParent[parentTheme] || [],
                          isValueSelected,
                          sortOptions,
                        )
                        const isExpanded =
                          themeExpandedState[parentTheme] ?? true
                        const isSelected = isParentThemeSelected(parentTheme)

                        return (
                          <div
                            key={parentTheme}
                            style={{ marginBottom: '4px' }}
                          >
                            {/* Parent Theme Header */}
                            <GroupHeaderRow
                              $padding="4px 8px"
                              $marginBottom="2px"
                            >
                              {/* Expand/Collapse Arrow */}
                              <ExpandMoreIcon
                                style={{
                                  marginRight: '8px',
                                  fontSize: '20px',
                                  color: '#6b7280',
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s ease-in-out',
                                  transform: isExpanded
                                    ? 'rotate(0deg)'
                                    : 'rotate(-90deg)',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setThemeExpandedState((prev) => ({
                                    ...prev,
                                    [parentTheme]: !isExpanded,
                                  }))
                                }}
                              />

                              {/* Checkbox */}
                              <SmallCheckbox
                                checked={isSelected}
                                onChange={() =>
                                  handleSelectParentTheme(parentTheme)
                                }
                                onClick={(e) => e.stopPropagation()}
                                style={{ marginRight: '8px' }}
                              />

                              {/* Label */}
                              <span
                                style={{
                                  color: '#1f2937',
                                  fontWeight: '500',
                                  fontSize: '13px',
                                  flex: 1,
                                }}
                                onClick={() =>
                                  setThemeExpandedState((prev) => ({
                                    ...prev,
                                    [parentTheme]: !isExpanded,
                                  }))
                                }
                              >
                                {parentTheme}{' '}
                                <span
                                  style={{
                                    fontWeight: '400',
                                    color: '#6b7280',
                                    fontSize: '12px',
                                  }}
                                >
                                  ({numberFormatter.format(childThemes.length)})
                                </span>
                              </span>
                            </GroupHeaderRow>

                            {/* Child Themes */}
                            {isExpanded &&
                              childThemes.map(
                                (value: FilterOptionValue, index: number) => {
                                  const existingFilter =
                                    getFilterSelection(selectedField)
                                  const selectedValues =
                                    existingFilter?.values || []
                                  // Use cluster_uuid for checking selection
                                  const themeIdentifier =
                                    getSpecialFilterIdentifier(
                                      value,
                                      selectedField,
                                    )
                                  const isChecked =
                                    selectedValues.includes(themeIdentifier)
                                  // Extract child theme name (part after ": ")
                                  const childThemeName =
                                    value.value.split(': ')[1] || value.value

                                  return (
                                    <HoverableValueRow
                                      key={`${parentTheme}-${value.value}-${index}`}
                                      $active={isChecked}
                                      style={{
                                        padding: '4px 8px 4px 36px', // Indented
                                        marginBottom: '2px',
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleValueToggle(value, useClusterUuid)
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <SmallCheckbox
                                          checked={isChecked}
                                          onChange={() => {}}
                                          style={{ marginRight: '12px' }}
                                        />
                                        <span
                                          style={{
                                            fontSize: '13px',
                                            color: '#1f2937',
                                          }}
                                        >
                                          {childThemeName}
                                        </span>
                                      </div>
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px',
                                        }}
                                      >
                                        <PaneRowOnlyButton
                                          className="row-action-btn"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            handleOnlyClick(
                                              value,
                                              useClusterUuid,
                                            )
                                          }}
                                        >
                                          only
                                        </PaneRowOnlyButton>
                                        <FilterOptionPostCountCell
                                          nPosts={value.n_posts}
                                        />
                                      </div>
                                    </HoverableValueRow>
                                  )
                                },
                              )}
                          </div>
                        )
                      })}
                    </>
                  ) : (
                    <>
                      {/* Non-brand, non-theme filters: Show Select All and regular list */}
                      {renderSelectAllRow(false)}

                      {/* Selected Values Display */}
                      {(() => {
                        const existingFilter = getFilterSelection(selectedField)
                        const selectedValues = existingFilter?.values || []
                        return selectedValues.length > 0 ? (
                          <div
                            style={{
                              backgroundColor: '#f3f4f6',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              marginBottom: '12px',
                              fontSize: '13px',
                            }}
                          >
                            <span style={{ color: '#6b7280' }}>Selected: </span>
                            <span
                              style={{
                                color: accents.reflectionBlue[500],
                                fontWeight: '500',
                              }}
                            >
                              {numberFormatter.format(selectedValues.length)}{' '}
                              value
                              {selectedValues.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        ) : null
                      })()}

                      {/* Virtualized value list — avoids main-thread block
                          when the field has thousands of values (e.g. Source
                          on a large project). selectedValuesForFieldSet gives
                          O(1) isChecked lookups instead of Array.includes per
                          row. Keyboard-nav scroll drives virtuosoRef so the
                          highlighted item is always in the viewport. */}
                      {sortedValuesForSelectedField.length > 0 ? (
                        <Virtuoso
                          ref={virtuosoRef}
                          style={{ flex: 1, minHeight: 0 }}
                          totalCount={sortedValuesForSelectedField.length}
                          itemContent={(index) => {
                            const value = sortedValuesForSelectedField[index]
                            if (!value) return null
                            const isHighlighted = highlightedIndex === index
                            const isChecked = selectedValuesForFieldSet.has(
                              useClusterUuid
                                ? getSpecialFilterIdentifier(
                                    value,
                                    selectedField,
                                  )
                                : value.value,
                            )
                            return (
                              <HoverableValueRow
                                $active={isHighlighted || isChecked}
                                style={{
                                  padding: '4px 8px',
                                  marginBottom: '4px',
                                }}
                                onClick={(event) => {
                                  event.stopPropagation()
                                  handleValueToggle(
                                    useClusterUuid ? value : value.value,
                                    useClusterUuid,
                                  )
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    minWidth: 0,
                                    flex: 1,
                                  }}
                                >
                                  {/* Checkbox */}
                                  <SmallCheckbox
                                    checked={isChecked}
                                    onChange={() => {}}
                                    style={{
                                      marginRight: '12px',
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span
                                    title={
                                      isNullValueAlias(value.value)
                                        ? ALIAS_FOR_NULL_VALUES
                                        : value.value
                                    }
                                    style={{
                                      fontSize: '13px',
                                      color: '#1f2937',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {isNullValueAlias(value.value)
                                      ? ALIAS_FOR_NULL_VALUES
                                      : value.value}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    flexShrink: 0,
                                  }}
                                >
                                  <PaneRowOnlyButton
                                    className="row-action-btn"
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      handleOnlyClick(
                                        useClusterUuid ? value : value.value,
                                        useClusterUuid,
                                      )
                                    }}
                                  >
                                    only
                                  </PaneRowOnlyButton>
                                  <FilterOptionPostCountCell
                                    nPosts={value.n_posts}
                                  />
                                </div>
                              </HoverableValueRow>
                            )
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            padding: '8px 12px',
                            color: '#6b7280',
                            fontSize: '13px',
                          }}
                        >
                          No values available for this field
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                // Show all child values (when "All" is selected)
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        margin: '0 0 8px 0',
                      }}
                    >
                      <h3
                        style={{
                          margin: 0,
                          fontSize: '14px',
                          fontWeight: '600',
                        }}
                      >
                        All Properties
                        {!isLoading && (
                          <span
                            style={{
                              fontWeight: '400',
                              color: '#6b7280',
                              marginLeft: '8px',
                            }}
                          >
                            (
                            {numberFormatter.format(
                              flattenedChildValues.length,
                            )}
                            )
                          </span>
                        )}
                      </h3>
                      {sortDropdown}
                    </div>
                    {isLoading ? (
                      <FilterOptionsSkeleton
                        role="status"
                        aria-label="Loading filter options"
                      >
                        {[72, 58, 84, 66, 76, 62, 80, 70].map(
                          (width, index) => (
                            <FilterOptionSkeletonRow key={index}>
                              <Skeleton
                                active
                                paragraph={false}
                                title={{ width: `${width}%` }}
                              />
                            </FilterOptionSkeletonRow>
                          ),
                        )}
                      </FilterOptionsSkeleton>
                    ) : flattenedChildValues.length > 0 ? (
                      <Virtuoso
                        ref={virtuosoRef}
                        style={{ flex: 1, minHeight: 0 }}
                        totalCount={flattenedChildValues.length}
                        itemContent={(index) => {
                          const item = flattenedChildValues[index]
                          if (!item) return null

                          const isHighlighted = highlightedIndex === index
                          const isChecked = isFlatItemChecked(item)
                          return (
                            <HoverableValueRow
                              key={`${item.parentField}-${item.value.value}-${index}`}
                              $active={isHighlighted || isChecked}
                              style={{
                                padding: '4px 8px',
                                marginBottom: '4px',
                              }}
                              onClick={(event) => {
                                event.stopPropagation()
                                // Apply filter directly without switching field view
                                toggleFlatItem(item)
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                {/* Checkbox */}
                                <SmallCheckbox
                                  checked={isChecked}
                                  onChange={() => {}}
                                  style={{ marginRight: '12px' }}
                                />
                                <span
                                  style={{
                                    fontSize: '13px',
                                    color: '#1f2937',
                                  }}
                                >
                                  {item.displayText}
                                </span>
                              </div>
                              {/* No trailing slot on this view: counts are
                                  fetched per expanded field and this view
                                  expands none, and the "only" action lives in
                                  each filter type's own value list (pick one
                                  on the left). A hidden-but-laid-out
                                  `.row-action-btn` here would reserve dead
                                  space at the row's right edge. PROD-3136. */}
                            </HoverableValueRow>
                          )
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          padding: '8px 12px',
                          color: '#6b7280',
                          fontSize: '13px',
                        }}
                      >
                        No values found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {requiresApply && (
          <ApplyDraftFooter>
            <SutraButton
              type="button"
              $variant="secondary"
              $size="sm"
              $height="30px"
              onClick={onClose}
            >
              Cancel
            </SutraButton>
            <SutraButton
              type="button"
              $variant="primary"
              $size="sm"
              $height="30px"
              disabled={changedDrafts.length === 0}
              onClick={handleApplyDraft}
            >
              Apply
            </SutraButton>
          </ApplyDraftFooter>
        )}
      </div>

      {/* Overlay to close popover when clicking outside */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1999,
        }}
        onClick={onClose}
      />
    </>,
    portalContainer,
  )
}
